import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useContext,
} from 'react';
import { Button, Input, Select, Tabs, Modal, message, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import Table from '@components/Table';
import useSwr from '@hooks/useSwr';
import moment from 'moment';
import api from '@services/api';
import {
  bytesFormat,
  dateFormat,
  toISOStringWithOffset,
} from '@utils/formatters';
import { IObject } from '@utils/defaultTypes';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import RequestReport from '@components/RequestReport';
import rolesConstants from '@utils/rolesConstants';
import useSearchParams from '@hooks/useSearchParams';
import SessionContext from '@providers/Session/SessionContext';
import * as S from '../styles';

interface IAlertsEvents {
  tableOnly?: boolean;
  defaultFilters?: IObject;
  isDetailsContracts?: boolean;
}

const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

const AlertsEvents = ({
  defaultFilters,
  tableOnly,
  isDetailsContracts,
}: IAlertsEvents) => {
  const { t } = useTranslation(['alerts', 'attributes']);
  const { keycloak } = useContext(KeycloakContext);
  const { isMot, isMotEnterprise, isMotPay, isMotBusiness } =
    useContext(SessionContext);

  const customerType = keycloak?.idTokenParsed?.customerType;
  const isEndCustomer = customerType === 'CUSTOMER';
  const isBroker = customerType === 'BROKER';

  const [msisdnValue, setMsisdnValue] = useState('');
  const [contractIdValue, setContractIdValue] = useState('');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['showContracted'],
    extraParams: [
      'eventType',
      'field',
      'dateFrom',
      'dateTo',
      'msisdn',
      'contractId',
    ],
  });

  const {
    dateFrom,
    dateTo,
    showContracted,
    msisdn,
    page,
    contractId,
    eventType,
    field,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const params = useMemo(() => {
    const allFilters = {
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
      msisdn,
      eventType,
      field,
      dateFrom: dateFrom && toISOStringWithOffset(dateFrom),
      dateTo: dateTo && toISOStringWithOffset(dateTo),
      contractId,
      showContracted,
      ...defaultFilters,
    };
    allFilters.showContracted = showContracted === 'true';

    if (isEndCustomer || isMotPay) {
      allFilters.showContracted = 'true';
    }

    if (!isMot) {
      delete allFilters.showContracted;
    }

    return allFilters;
  }, [
    page,
    linesPerPage,
    msisdn,
    eventType,
    field,
    dateFrom,
    dateTo,
    contractId,
    showContracted,
    defaultFilters,
    isEndCustomer,
    isMotPay,
    isMot,
  ]);

  const { data, mutate } = useSwr(
    `/service-proxy/alert-events`,
    {
      params,
    },
    {
      refreshInterval: 300000,
    },
  );

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/service-proxy/alert-events/${id}`);
        message.success(t('msgs.alert-event-deleted'));
        mutate();
      } catch {
        message.error(t('msgs.alert-event-deletion-error'));
      }
    },
    [t, mutate],
  );

  const showDelConfirm = useCallback(
    (idToDelete: string, nameToChange: string) => {
      confirm({
        title: t('titles.delete-modal-event', { nameToChange }),
        icon: <ExclamationCircleOutlined />,
        content: t('titles.delete-modal-content-event'),
        centered: true,
        okText: t('buttons.yes'),
        cancelText: t('buttons.no'),
        okButtonProps: { type: 'primary', danger: true },
        onOk: () => handleDelete(idToDelete),
      });
    },
    [handleDelete, t],
  );

  const adminActions = useCallback(
    (text: string, record: any) => (
      <Button
        icon={<DeleteOutlined />}
        danger
        onClick={() => showDelConfirm(record.id, record.id)}
      />
    ),
    [showDelConfirm],
  );

  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', align: 'center' },
      { title: t('table.alertId'), dataIndex: 'alertId', align: 'center' },
      ...(isMotEnterprise
        ? [
            {
              title: t('table.contractId'),
              dataIndex: 'contractId',
              align: 'center',
            },
          ]
        : []),
      {
        title: 'MSISDN',
        dataIndex: 'msisdn',
        align: 'center',
      },
      {
        title: t('table.eventType'),
        dataIndex: 'eventType',
        align: 'center',
        render: (value: string) => t(`eventType.${value}`),
      },
      {
        title: t('table.eventValue'),
        dataIndex: 'eventValue',
        align: 'center',
        render: (value: number, record: any) => {
          if (record.field === 'consumptionPercent') {
            return `${value.toFixed(1)}%`;
          }

          return bytesFormat(value);
        },
      },
      {
        title: t('table.field'),
        dataIndex: 'field',
        align: 'center',
        render: (value: string) => t(`field.${value}`),
      },
      {
        title: t('table.thresholdValue'),
        dataIndex: 'thresholdValue',
        align: 'center',
        render: (value: number, record: any) => {
          if (record.field === 'consumptionPercent') {
            return `${value}%`;
          }
          if (
            record.field === 'consumptionTotalAnomaly' ||
            record.field === 'consumptionCurrentDayAnomaly'
          ) {
            return `${value * 100}%`;
          }
          return bytesFormat(value);
        },
      },
      {
        title: t('attributes:name'),
        dataIndex: 'name',
        align: 'center',
      },
      {
        title: t('table.firedAt'),
        dataIndex: 'createdAt',
        align: 'center',
        render: (value: string) => value && dateFormat(value),
      },
      {
        title: t('attributes:actions'),
        dataIndex: 'actions',
        align: 'center',
        render: adminActions,
      },
    ],
    [t, adminActions, isMotEnterprise],
  );

  const handleDateChange = useCallback(
    (date: any) => {
      const initial = date && date[0].toISOString();
      const end = date && date[1].toISOString();
      setSearchParams({ dateFrom: initial, dateTo: end });
    },
    [setSearchParams],
  );

  const ranges: Record<string, [moment.Moment, moment.Moment]> = useMemo(() => {
    return {
      [t('attributes:lastAlertDateFrom.lastThreeHours')]: [
        moment().subtract(3, 'hours'),
        moment(),
      ],
      [t('attributes:lastAlertDateFrom.lastSixHours')]: [
        moment().subtract(6, 'hours'),
        moment(),
      ],
      [t('attributes:lastAlertDateFrom.lastTwelveHours')]: [
        moment().subtract(12, 'hours'),
        moment(),
      ],
      [t('attributes:lastAlertDateFrom.lastDay')]: [
        moment().subtract(1, 'day'),
        moment(),
      ],
      [t('attributes:lastAlertDateFrom.lastWeek')]: [
        moment().subtract(7, 'days'),
        moment(),
      ],
      [t('attributes:lastAlertDateFrom.lastMonth')]: [
        moment().subtract(1, 'months'),
        moment(),
      ],
    };
  }, [t]);

  const tableFilters = useMemo(
    () => [
      {
        visible: !tableOnly,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('MSISDN'),
        element: (
          <Search
            onSearch={value => setSearchParams({ msisdn: value })}
            onChange={({ target: { value } }) => setMsisdnValue(value)}
            value={msisdnValue}
            allowClear
          />
        ),
      },
      {
        visible: !tableOnly && isMotEnterprise,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.contractId'),
        element: (
          <Search
            onSearch={value => setSearchParams({ contractId: value })}
            onChange={({ target: { value } }) => setContractIdValue(value)}
            value={contractIdValue}
            allowClear
          />
        ),
      },
      {
        visible: !tableOnly,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.eventType'),
        element: (
          <Select
            value={eventType}
            onChange={value => setSearchParams({ eventType: value })}
            style={{ width: '100%' }}
            allowClear
          >
            <Option value="CONSUMPTION">{t('eventType.CONSUMPTION')}</Option>
            {isMotEnterprise && (
              <Option value="CONTRACT_CONSUMPTION">
                {t('eventType.CONTRACT_CONSUMPTION')}
              </Option>
            )}
          </Select>
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.field'),
        element: (
          <Select
            value={field}
            onChange={value => setSearchParams({ field: value })}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="consumptionTotal">
              {t('field.consumptionTotal')}
            </Option>
            <Option value="consumptionPercent">
              {t('field.consumptionPercent')}
            </Option>

            <Option value="consumptionTotalAnomaly">
              {t('field.consumptionTotalAnomaly')}
            </Option>
            {!isDetailsContracts && (
              <>
                {' '}
                <Option value="consumptionCurrentDayAnomaly">
                  {t('field.consumptionCurrentDayAnomaly')}
                </Option>
                <Option value="consumptionCurrentDay">
                  {t('field.consumptionCurrentDay')}
                </Option>
              </>
            )}
          </Select>
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.firedAt'),
        element: (
          <RangePicker
            showTime
            onChange={handleDateChange}
            ranges={ranges}
            format="DD/MM/YYYY HH:mm"
            defaultValue={
              dateFrom
                ? [
                    moment(dateFrom, 'YYYYMMDD HH:mm:ss'),
                    moment(dateTo, 'YYYYMMDD HH:mm:ss'),
                  ]
                : undefined
            }
            style={{ width: '100%' }}
          />
        ),
      },
    ],
    [
      t,
      setSearchParams,
      msisdnValue,
      dateFrom,
      dateTo,
      handleDateChange,
      field,
      eventType,
      tableOnly,
      contractIdValue,
      isDetailsContracts,
      ranges,
      isMotEnterprise,
    ],
  );

  useEffect(() => {
    setMsisdnValue(msisdn);
  }, [msisdn]);

  useEffect(() => {
    setContractIdValue(contractId);
  }, [contractId]);

  return (
    <S.AlertsContainer>
      {!tableOnly && !isMotPay && !isEndCustomer && (
        <Tabs
          type="card"
          defaultActiveKey="false"
          activeKey={showContracted}
          onChange={key => {
            setSearchParams({
              showContracted: key,
            });
          }}
          style={{ background: '#ffffff' }}
        >
          {(() => {
            const commonTabs = (
              <>
                <TabPane
                  key="false"
                  tab={t(`visions.${isMotBusiness ? 'allocated' : 'false'}`)}
                />
                <TabPane key="true" tab={t('visions.true')} />
              </>
            );

            if (isEndCustomer) {
              return <TabPane key="true" tab={t('visions.true')} />;
            }

            if (isBroker) {
              return commonTabs;
            }

            return commonTabs;
          })()}
        </Tabs>
      )}

      {isDetailsContracts &&
      (!data?.content || data?.content?.length === 0) &&
      !field &&
      !dateFrom &&
      !dateTo ? (
        <> </>
      ) : (
        <Table
          title={tableOnly ? t('titles.events-alerts') : t('titles.events')}
          // @ts-ignore
          columns={columns}
          data={data?.content}
          loading={!data?.content}
          tableKey="AlertsEvents"
          pagination={{ page, linesPerPage, total: data?.totalElements }}
          filters={{
            list: tableFilters,
            hasValuesInFilters: hasValuesInExtraParams,
            onClearFilters: () => clearSearchParams(),
          }}
          onTableChange={setSearchParams}
          extraActions={
            <>
              <RequestReport
                items={[
                  {
                    role: rolesConstants.REQUEST_REPORT,
                    path: '/service-proxy/alert-events/report',
                    type: 'ALERT_EVENTS',
                    filters: {
                      msisdn,
                      eventType,
                      field,
                      dateFrom,
                      dateTo,
                      contractId,
                      showContracted: showContracted === 'true',
                      ...defaultFilters,
                    },
                    method: 'get',
                  },
                ]}
              />
            </>
          }
        />
      )}
    </S.AlertsContainer>
  );
};

export default AlertsEvents;
